import {
  hasMarketingDirectEntryQueryParams,
  useHistory,
  useQueryParams,
  useQueryParamsCallback,
} from '@aboutyou/router';
import {useEffect} from 'react';

export type QuickViewVariant = 'adp' | 'quickView' | 'quickViewV2';

// eslint-disable-next-line spaced-comment
let serverVariant: QuickViewVariant | null = /*@__PURE__*/ (() => {
  if (!process.browser) {
    return null;
  }
  try {
    const elem = document.querySelector<HTMLSpanElement>(
      '[data-quick-view-variant]',
    );
    const value = elem?.dataset.quickViewVariant;
    if (value === 'v2') return 'quickViewV2';
    if (value === 'v1') return 'quickView';
    if (value === 'adp') return 'adp';

    const legacy = document.querySelector<HTMLSpanElement>(
      '[data-quick-view-enabled]',
    );
    const legacyValue = legacy?.dataset.quickViewEnabled;
    if (legacyValue === 'true') return 'quickView';
    if (legacyValue === 'false') return 'adp';
    return null;
  } catch (_e) {
    return null;
  }
})();

const resetServerVariant = () => {
  serverVariant = null;
};

export function useQuickViewVariant(): QuickViewVariant {
  const browserVariant = useBrowserQuickViewVariant();
  const history = useHistory();
  const getParams = useQueryParamsCallback();

  useEffect(() => {
    const differs = serverVariant !== null && browserVariant !== serverVariant;
    if (differs) {
      const newSearch = new URLSearchParams(getParams());
      if (serverVariant === 'quickView') {
        newSearch.set('quick', 'view');
      } else if (serverVariant === 'quickViewV2') {
        newSearch.set('quick', 'view1');
      } else {
        newSearch.delete('quick');
      }
      history.replace({...history.location, search: newSearch.toString()});
    }
    resetServerVariant();
  }, []);

  if (serverVariant !== null) {
    return serverVariant;
  }

  return browserVariant;
}

/**
 * Internal URL-only variant resolver. Not exported on purpose: reading the
 * variant straight from the URL skips the SSR-marker + history reconciliation
 * in `useQuickViewVariant`, which previously led to hydration mismatches.
 * All consumers must go through `useQuickViewVariant` instead.
 */
const useBrowserQuickViewVariant = (): QuickViewVariant => {
  const params = useQueryParams({includeUtmParams: true});

  if (params.get('view') === 'adp') {
    return 'adp';
  }

  const quickParam = params.get('quick');
  if (quickParam === 'view1') return 'quickViewV2';
  if (quickParam === 'view') return 'quickView';

  if (hasMarketingDirectEntryQueryParams(params)) return 'quickView';

  return 'adp';
};
